import { useState, useEffect } from 'react';
import { useAuth } from '../../../AuthContext';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Refresh, GetApp, ArrowBack } from '@mui/icons-material';
import { IconButton, Tooltip, Alert, Snackbar } from '@mui/material';
import { useHistory } from 'react-router-dom';
import { exportStyledExcel } from '../../../utils/excelExport';
import { formatDate as fmtDate } from '../../../utils/dateUtils';

const fmt = (n) => `₹${(parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const today = new Date();
today.setHours(0, 0, 0, 0);

const daysDiff = (dateStr) => {
  const d = new Date(dateStr);
  d.setHours(0, 0, 0, 0);
  return Math.floor((today - d) / (1000 * 60 * 60 * 24));
};

const bucket = (days) => {
  if (days <= 30)  return '0-30';
  if (days <= 60)  return '31-60';
  if (days <= 90)  return '61-90';
  return '90+';
};

const VendorAgeingReport = () => {
  useSessionValidation();
  const { companyId, branchId, token, companyName, username } = useAuth();
  const history = useHistory();

  const fmt2Date = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const now = new Date();
  const [fromDate, setFromDate] = useState(fmt2Date(new Date(now.getFullYear() - 1, now.getMonth(), 1)));
  const [toDate, setToDate]     = useState(fmt2Date(now));
  const [rows, setRows]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [fetched, setFetched]   = useState(false);

  const fetchReport = async () => {
    if (new Date(fromDate) > new Date(toDate)) { setError('From Date cannot be greater than To Date'); return; }
    setLoading(true);
    try {
      const res = await fetchData('ReportApi', `/PurchaseReport/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
      const data = Array.isArray(res.response_data || res) ? (res.response_data || res) : [];

      // Group by supplier, only outstanding (balance > 0)
      const map = {};
      data.forEach((r) => {
        const bal = parseFloat(r.BalanceAmount) || 0;
        if (bal <= 0) return;
        const name = r.MasterName || 'Unknown';
        if (!map[name]) map[name] = { supplier: name, bills: [], total: 0, b0_30: 0, b31_60: 0, b61_90: 0, b90plus: 0 };
        const days = daysDiff(r.BillDate);
        const b = bucket(days);
        map[name].total += bal;
        if (b === '0-30')  map[name].b0_30   += bal;
        if (b === '31-60') map[name].b31_60  += bal;
        if (b === '61-90') map[name].b61_90  += bal;
        if (b === '90+')   map[name].b90plus += bal;
        map[name].bills.push({ billNo: r.BillNumber, date: r.BillDate, amount: parseFloat(r.TotalAmount) || 0, paid: parseFloat(r.PaidAmount) || 0, balance: bal, days });
      });

      setRows(Object.values(map).sort((a, b) => b.total - a.total));
      setFetched(true);
    } catch (err) {
      console.error(err);
      setError('Failed to fetch ageing report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId && branchId && token && fromDate && toDate) {
      if (new Date(fromDate) > new Date(toDate)) {
        setData([]);
        setFetched(false);
        setError('From Date cannot be greater than To Date');
        return;
      }
      fetchReport();
    }
  }, [companyId, branchId, token, fromDate, toDate]);

  const totals = rows.reduce((acc, r) => ({
    total:   acc.total   + r.total,
    b0_30:   acc.b0_30   + r.b0_30,
    b31_60:  acc.b31_60  + r.b31_60,
    b61_90:  acc.b61_90  + r.b61_90,
    b90plus: acc.b90plus + r.b90plus,
  }), { total: 0, b0_30: 0, b31_60: 0, b61_90: 0, b90plus: 0 });

  const handleExport = async () => {
    if (!rows.length) return;
    const generatedAt = new Date().toLocaleString('en-IN');
    const colHeaders = ['#', 'Supplier', 'Total Outstanding', '0-30 Days', '31-60 Days', '61-90 Days', '90+ Days'];

    const dataRows = rows.map((r, i) => [
      i + 1, r.supplier,
      r.total, r.b0_30, r.b31_60, r.b61_90, r.b90plus,
    ]);

    const totalsRow = ['', 'Totals', totals.total, totals.b0_30, totals.b31_60, totals.b61_90, totals.b90plus];

    const numCols = colHeaders.length;

      await exportStyledExcel({
          reportTitle: 'VENDOR AGEING REPORT',
          sheetName: 'Vendor Ageing',
          fileName: `VendorAgeingReport_${fromDate}_${toDate}.xlsx`,
          companyName,
          username,
          fromDate,
          toDate,
          headers: colHeaders,
          rows: dataRows,
            totalsRow: totalsRow,
          columnWidths: [4,28,16,12,12,12,12],
      });
  };

  const th  = { padding: '8px 10px', textAlign: 'left',  fontWeight: 600, fontSize: 11, whiteSpace: 'nowrap', borderBottom: '1px solid rgba(255,255,255,0.2)', background: '#e8734a', color: 'white' };
  const thR = { ...th, textAlign: 'right' };
  const td  = { padding: '7px 10px', textAlign: 'left',  fontSize: 12, whiteSpace: 'nowrap' };
  const tdR = { padding: '7px 10px', textAlign: 'right', fontSize: 12, whiteSpace: 'nowrap' };

  const bucketColor = (val) => val > 0 ? '#dc2626' : '#374151';

  return (
    <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 12, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
      <LogoLoader open={loading} message="Loading vendor ageing report..." />

      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>{error}</Alert>
      </Snackbar>

      <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>

        {/* Toolbar */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', background: 'linear-gradient(135deg, #d4500a 0%, #e8734a 100%)', gap: 6, flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none' }}>
          <Tooltip title="Back" arrow>
            <IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={{ border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px', height: 29, width: 29, color: '#fff', flexShrink: 0, '&:hover': { background: 'rgba(255,255,255,0.1)' } }}>
              <ArrowBack sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', flexShrink: 0 }}>Vendor Ageing Report</span>
          <div style={{ flex: 1, minWidth: 4 }} />
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
          <Tooltip title="Refresh" arrow>
            <IconButton onClick={fetchReport} disabled={loading} size="small" sx={{ border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px', height: 29, width: 29, color: '#fff', flexShrink: 0, '&:hover': { background: 'rgba(255,255,255,0.1)' } }}>
              <Refresh sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export Excel" arrow>
            <IconButton onClick={handleExport} disabled={!rows.length} size="small" sx={{ border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px', height: 29, width: 29, color: '#fff', flexShrink: 0, '&:hover': { background: 'rgba(255,255,255,0.1)' }, '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' } }}>
              <GetApp sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </div>

        {/* Scrollable Table */}
        <div className="report-table-scroll" style={{ overflow: 'auto', overflowY: 'scroll', maxHeight: 'calc(100vh - 130px)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', minWidth: 700 }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
              <tr>
                <th style={th}>#</th>
                <th style={th}>Supplier</th>
                <th style={thR}>Total Outstanding</th>
                <th style={{ ...thR, background: '#1e6b3e' }}>0 - 30 Days</th>
                <th style={{ ...thR, background: '#b45309' }}>31 - 60 Days</th>
                <th style={{ ...thR, background: '#b91c1c' }}>61 - 90 Days</th>
                <th style={{ ...thR, background: '#7f1d1d' }}>90+ Days</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.supplier} style={{ background: i % 2 === 0 ? '#fff' : '#fbe9e7', borderBottom: '1px solid #f0f0f0' }}>
                  <td style={td}>{i + 1}</td>
                  <td style={{ ...td, fontWeight: 600, color: '#bf360c' }}>{r.supplier}</td>
                  <td style={{ ...tdR, fontWeight: 700, color: '#dc2626' }}>{fmt(r.total)}</td>
                  <td style={{ ...tdR, color: bucketColor(r.b0_30) }}>{fmt(r.b0_30)}</td>
                  <td style={{ ...tdR, color: bucketColor(r.b31_60) }}>{fmt(r.b31_60)}</td>
                  <td style={{ ...tdR, color: bucketColor(r.b61_90) }}>{fmt(r.b61_90)}</td>
                  <td style={{ ...tdR, color: bucketColor(r.b90plus), fontWeight: r.b90plus > 0 ? 700 : 400 }}>{fmt(r.b90plus)}</td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', padding: '24px', color: '#9ca3af', fontSize: 13 }}>
                    {fetched ? 'No outstanding balances found for the selected date range' : 'Select date range to load report'}
                  </td>
                </tr>
              )}
            </tbody>
            {rows.length > 0 && (
              <tfoot>
                <tr style={{ background: '#ffccbc', color: '#bf360c', fontWeight: 700, position: 'sticky', bottom: 0 }}>
                  <td colSpan={2} style={{ ...td, textAlign: 'right', fontWeight: 700 }}>Totals</td>
                  <td style={{ ...tdR, fontWeight: 700 }}>{fmt(totals.total)}</td>
                  <td style={tdR}>{fmt(totals.b0_30)}</td>
                  <td style={tdR}>{fmt(totals.b31_60)}</td>
                  <td style={tdR}>{fmt(totals.b61_90)}</td>
                  <td style={{ ...tdR, fontWeight: 700 }}>{fmt(totals.b90plus)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

export default VendorAgeingReport;


